/**
 * The Workshop Dashboards routes inside admin mode.
 *
 * No shell layout route any more. Sales, Teachers, Supplies and Corp Events
 * each own their header and their region scope, the way Overview and Venues
 * already did, so there is nothing left for a parent route to hold — and
 * nothing for a second, region-rooted copy of this table to exist for.
 *
 * Each page carries the same gate as the endpoint behind it. Regions is
 * Admin's alone: `read:regions` is granted to no other role.
 */
import { Route } from 'react-router-dom';

import RequirePermission from '../../../shared/RequirePermission';
import { Permission } from '../../../types/permissions';
import DailySalesPage from '../daily-sales/DailySalesPage';
import { DashboardsPage } from '../DashboardsPage';
import CheckInModalRoute from '../workshops/detail/CheckInModalRoute';
import WorkshopDetailPage from '../workshops/WorkshopDetailPage';

import CorpEventsPage from './CorpEventsPage';
import AdminRegions from './regions/AdminRegions';
import AdminRegionTriage from './regions/AdminRegionTriage';
import AdminRegionVenues from './regions/AdminRegionVenues';
import RegionBuilder from './regions/RegionBuilder';
import RegionDetailPage from './regions/RegionDetailPage';
import { RegionsPage } from './regions/RegionsPage';
import SalesPage from './SalesPage';
import SuppliesPage from './SuppliesPage';
import TeachersPage from './TeachersPage';

const readDashboards = (element: React.ReactNode) => (
  <RequirePermission anyOf={[Permission.READ_DASHBOARDS, Permission.READ_ALL]}>
    {element}
  </RequirePermission>
);

const readSalesReports = (element: React.ReactNode) => (
  <RequirePermission anyOf={[Permission.READ_SALES_REPORTS, Permission.READ_ALL]}>
    {element}
  </RequirePermission>
);

const readRegions = (element: React.ReactNode) => (
  <RequirePermission permission={Permission.READ_REGIONS}>
    <RegionsPage>{element}</RegionsPage>
  </RequirePermission>
);

/** The four region-scoped buckets. */
const bucketRoutes = (
  <>
    <Route path="sales" element={readDashboards(<SalesPage />)} />
    <Route path="teachers" element={readDashboards(<TeachersPage />)} />
    <Route path="supplies" element={readDashboards(<SuppliesPage />)} />
    <Route path="corp" element={readDashboards(<CorpEventsPage />)} />
  </>
);

/** Region management — the hierarchy every other page's scope picker reads. */
const regionRoutes = (
  <>
    <Route path="regions" element={readRegions(<AdminRegions />)} />
    <Route path="regions/venues" element={readRegions(<AdminRegionVenues />)} />
    <Route path="regions/triage" element={readRegions(<AdminRegionTriage />)} />
    <Route path="regions/:id" element={readRegions(<RegionDetailPage />)} />
    <Route path="regions/:id/edit" element={readRegions(<RegionBuilder />)} />
  </>
);

export const adminDashboardsRoutes = (
  <>
    {bucketRoutes}
    <Route path="finance/daily-sales" element={readSalesReports(<DailySalesPage />)} />
    {regionRoutes}
    {/* The detail page carries no chrome of its own — under the old shells it
        inherited `.nb-dashboards` (the whole token set AND the page
        background). Standalone it has to be given one, or every var resolves
        to nothing and the page renders unstyled. */}
    <Route
      path="workshops/:workshopId"
      element={
        <DashboardsPage stack={false}>
          <WorkshopDetailPage />
        </DashboardsPage>
      }
    >
      <Route path="checkin" element={<CheckInModalRoute />} />
    </Route>
  </>
);
